import { Checkbox } from "@/core/components/ui/checkbox";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { dayjs } from "@/core/utils/date";
import { ApprovalMark } from "@/payroll/approvals/approval-mark";
import type {
  IResolvedSheetApprovalUnit,
  ISheetApprovalRecipient,
} from "./types";
import { getRecipientUnits } from "./utils";

interface RequestApprovalRecipientRowProps {
  recipient: ISheetApprovalRecipient;
  units: IResolvedSheetApprovalUnit[];
  showStatus: boolean;
  checked: boolean;
  onToggle: (userId: string) => void;
}

export const RequestApprovalRecipientRow = ({
  recipient,
  units,
  showStatus,
  checked,
  onToggle,
}: RequestApprovalRecipientRowProps) => {
  const ownedUnits = getRecipientUnits(recipient, units);
  const emailedAt = recipient.latest_delivery?.sent_at;
  const checkboxId = `approval-recipient-${recipient.user_id}`;

  return (
    <Stack
      direction="row"
      align="start"
      gap="sm"
      fullWidth
      className="px-4 py-3 transition-colors hover:bg-surface-secondary"
    >
      <Checkbox
        id={checkboxId}
        checked={checked}
        onCheckedChange={() => onToggle(recipient.user_id)}
        className="mt-0.5 shrink-0"
      />
      <label htmlFor={checkboxId} className="min-w-0 flex-1 cursor-pointer">
        <Stack gap="xs" fullWidth>
          <Stack direction="row" align="baseline" gap="xs" fullWidth>
            <Typography variant="bodyW3" className="shrink-0 font-semibold">
              {recipient.name}
            </Typography>
            <Typography
              variant="bodyW3"
              textColor="tertiary"
              className="min-w-0 truncate"
              title={recipient.email ?? undefined}
            >
              {recipient.email}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            align="baseline"
            justify="between"
            gap="sm"
            fullWidth
          >
            <Stack
              direction="row"
              align="center"
              gap="md"
              className="min-w-0 flex-wrap"
            >
              {ownedUnits.map((unit) => (
                <Stack
                  key={unit.access_key}
                  direction="row"
                  align="center"
                  gap="xs"
                >
                  {showStatus && <ApprovalMark accessKey={unit} withTooltip />}
                  <Typography
                    variant="bodyW3"
                    textColor="secondary"
                    title={unit.sheet_names.join(", ")}
                  >
                    {unit.name} · {unit.sheet_names.length}{" "}
                    {unit.sheet_names.length === 1 ? "sheet" : "sheets"}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            {emailedAt && (
              <Typography
                variant="bodyW3"
                textColor="tertiary"
                className="shrink-0 whitespace-nowrap"
              >
                Emailed {dayjs(emailedAt).fromNow()}
              </Typography>
            )}
          </Stack>
        </Stack>
      </label>
    </Stack>
  );
};
